import { useEffect, useState } from 'react'
import CampaignLayout from 'Layouts/CampaignLayout'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import {
    fetchRecommendedCreators,
    inviteCreatorsToCampaign,
} from 'services/api/campaign'
import { useGetCampaignDataById } from 'slices/campaigns/selector'
import {
    defaultRecommendedCreatorsFilters,
    updateRecommendedCreatorsFilters,
} from 'slices/campaigns/recommended-creators/reducer'
import { fetchRecommendedCreatorsThunk } from 'slices/campaigns/recommended-creators/thunk'
import { AppDispatch, RootState } from 'store'
import { ERROR } from 'types'
import { generateUniqueKey, getErrorMessage } from 'utils/helpers'
import { getNextDiscoveryCreatorSort } from 'types/creator-discovery'
import DiscoveredCreatorsResults from 'Components/creator-discovery/DiscoveredCreatorsResults'
import { buildDiscoveredCreatorsCsv, downloadCsvFile } from 'utils/creatorDiscoveryCsvExport'
import 'pages/CreatorDiscovery/creator-discovery.scss'
import './recommended-creators.scss'

const RecommendedCreators = () => {
    const { campaignId } = useParams()
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const campaign = useGetCampaignDataById(campaignId!)
    const agencyId = useSelector((state: RootState) => state.auth.selectedAgencyId)
    const recommendationAvailable = Boolean(campaign?.creator_recommendation_available)
    const recommendationInitiated = Boolean(campaign?.creator_recommendation_initiated)
    const canViewRecommendedCreators =  recommendationAvailable && recommendationInitiated

    const [selectedCreatorIds, setSelectedCreatorIds] = useState<string[]>([])
    const [downloading, setDownloading] = useState(false)
    const [inviting, setInviting] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [filterId, setFilterId] = useState(
        generateUniqueKey(defaultRecommendedCreatorsFilters, ['page'])
    )

    const { filters, loading, error } = useSelector(
        (state: RootState) => state.campaignRecommendedCreators
    )
    const listData = useSelector(
        (state: RootState) =>
            state.campaignRecommendedCreators.campaigns[campaignId!]?.filtered_lists?.[filterId]
    )
    const creators = listData?.list ?? []
    const count = listData?.count ?? 0
    const page = listData?.page ?? 1
    const hasMore = listData?.has_more ?? true

    useEffect(() => {
        if (campaign && !canViewRecommendedCreators) {
            navigate(`/campaign/${campaignId}/applications`, { replace: true })
        }
    }, [campaign, canViewRecommendedCreators, campaignId, navigate])

    useEffect(() => {
        if (!campaignId || !canViewRecommendedCreators) {
            setInitialLoading(false)
            return
        }

        if (filters.page === 1) {
            setInitialLoading(true)
        }

        dispatch(fetchRecommendedCreatorsThunk({ campaignId, filters }))
            .unwrap()
            .catch((err) => {
                if (err !== ERROR.CACHED) {
                    toast.error(typeof err === 'string' ? err : 'Failed to load recommended creators')
                }
            })
            .finally(() => {
                setFilterId(generateUniqueKey(filters, ['page']))
                setInitialLoading(false)
            })
    }, [campaignId, canViewRecommendedCreators, filters, dispatch])

    useEffect(() => {
        setSelectedCreatorIds([])
        dispatch(updateRecommendedCreatorsFilters(defaultRecommendedCreatorsFilters))
    }, [campaignId, dispatch])

    const handleSortToggle = () => {
        const nextSort = getNextDiscoveryCreatorSort(filters.sort)
        setSelectedCreatorIds([])
        dispatch(updateRecommendedCreatorsFilters({ page: 1, sort: nextSort }))
    }

    const handleLoadMore = () => {
        if (!campaignId || !hasMore || loading) return
        dispatch(
            fetchRecommendedCreatorsThunk({
                campaignId,
                filters: { ...filters, page: page + 1 },
            })
        )
    }

    const handleToggleSelect = (creatorId: string) => {
        setSelectedCreatorIds((prev) =>
            prev.includes(creatorId) ? prev.filter((id) => id !== creatorId) : [...prev, creatorId]
        )
    }

    const handleClearSelection = () => setSelectedCreatorIds([])

    const handleDownload = async () => {
        if (!campaignId || !agencyId || downloading || count === 0) return

        setDownloading(true)
        try {
            const { items } = await fetchRecommendedCreators(
                agencyId,
                campaignId,
                1,
                filters.sort,
                count
            )
            const csv = buildDiscoveredCreatorsCsv(items)
            const date = new Date().toISOString().split('T')[0]
            downloadCsvFile(csv, `recommended_creators_${campaignId}_${date}.csv`)
            toast.success('Creators list downloaded successfully.')
        } catch (err) {
            toast.error(getErrorMessage(err, 'Failed to download creators'))
        } finally {
            setDownloading(false)
        }
    }

    const handleInvite = async () => {
        if (!campaignId || !agencyId || selectedCreatorIds.length === 0 || inviting) return

        const result = await Swal.fire({
            title: 'Invite selected creators?',
            text: `Invite ${selectedCreatorIds.length} creator${selectedCreatorIds.length === 1 ? '' : 's'} to this campaign?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Invite',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        setInviting(true)
        try {
            await inviteCreatorsToCampaign(agencyId, campaignId, selectedCreatorIds)
            toast.success(
                `${selectedCreatorIds.length} creator${selectedCreatorIds.length === 1 ? '' : 's'} invited successfully.`
            )
            setSelectedCreatorIds([])
        } catch (err) {
            toast.error(getErrorMessage(err, 'Failed to invite creators'))
        } finally {
            setInviting(false)
        }
    }


    return (
        <CampaignLayout>
            <div className="recommended-creators-results">
                <DiscoveredCreatorsResults
                    title="Recommended Creators"
                    creators={creators}
                    count={count}
                    loading={initialLoading || (loading && creators.length === 0)}
                    sort={filters.sort}
                    selectedIds={selectedCreatorIds}
                    showSummary={false}
                    scrollableTarget="recommended-creators-scroll"
                    downloading={downloading}
                    inviting={inviting}
                    inviteLabel="Invite to Campaign"
                    inviteDisabledTooltip={
                        count === 0
                            ? 'No recommended creators found yet.'
                            : 'Select at least one creator to invite them to this campaign.'
                    }
                    emptyTitle="No recommended creators"
                    emptyText="We couldn't find recommended creators for this campaign yet."
                    onSortToggle={handleSortToggle}
                    onToggleSelect={handleToggleSelect}
                    onClearSelection={handleClearSelection}
                    onLoadMore={handleLoadMore}
                    onDownload={handleDownload}
                    onInvite={handleInvite}
                />
                {error && creators.length > 0 && (
                    <div className="d-flex justify-content-center py-3">
                        <button
                            type="button"
                            className="theme-btn theme-btn-sm btn-primary-outlined"
                            onClick={handleLoadMore}
                        >
                            Load More <i className="ms-2 fs-5 bx bx-refresh" />
                        </button>
                    </div>
                )}
            </div>
        </CampaignLayout>
    )
}

export default RecommendedCreators
